import { useState, useCallback, useEffect } from 'react'
import { FaHome, FaCog } from 'react-icons/fa'
import RealIncomeCalculator from './components/RealIncomeCalculator'
import MarketComparison from './components/MarketComparison'
import AffordabilityAnalysis from './components/AffordabilityAnalysis'
import CalculatorForm from './components/CalculatorForm'
import ResultsDisplay from './components/ResultsDisplay'
import { 
  IncomeData,
  LegacyIncomeData, 
  RegionalFactors, 
  TradeSpecificFactors, 
  CalculatorResults,
  MarketComparison as MarketComparisonType,
  AffordabilityAnalysis as AffordabilityAnalysisType
} from './types'
import {
  calculateTotalGrossIncome,
  calculateAdjustedMonthlyIncome,
  calculateAffordabilityAnalysis,
  calculateGrossMonthlyIncome,
  calculateTaxes,
  calculateNetMonthlyIncome,
  calculateHousingBudget,
  generateRecommendations,
  validateIncomeData,
  validateNewIncomeData
} from './utils/calculations'
import { CALCULATION_DEFAULTS } from './utils/constants'

function App() {
  // New income data structure
  const [income, setIncome] = useState<Partial<IncomeData>>({
    primaryEarnerBaseSalary: 0,
    overtimeFactor: 0.15, // Default 15%
    bonusIncentiveAnnual: 0,
    secondaryEarnerSalary: 0,
    sideWorkFreelanceMonthly: 0,
    regionalCostAdjustmentFactor: 1.0
  })
  
  // Legacy income for backward compatibility
  const [legacyIncome, setLegacyIncome] = useState<Partial<LegacyIncomeData>>({
    weeksPerYear: CALCULATION_DEFAULTS.weeksPerYear,
    overtimeMultiplier: 1.5
  })
  
  const [regional, setRegional] = useState<Partial<RegionalFactors>>({
    cityType: 'suburban'
  })
  const [tradeFactors, setTradeFactors] = useState<Partial<TradeSpecificFactors>>({})
  const [results, setResults] = useState<CalculatorResults | null>(null)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  
  // New state for enhanced components
  const [marketComparison, setMarketComparison] = useState<MarketComparisonType>({})
  const [affordabilityAnalysis, setAffordabilityAnalysis] = useState<AffordabilityAnalysisType | null>(null)
  const [activeTab, setActiveTab] = useState<'new' | 'legacy'>('new')
  
  // Iframe integration state
  const [isEmbedded, setIsEmbedded] = useState(false)

  // Calculate new income structure affordability
  const handleNewCalculate = useCallback(() => {
    setErrors([])
    
    const validationErrors = validateNewIncomeData(income)
    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      return
    }

    if (!regional.state) {
      setErrors(['Please select your state'])
      return
    }

    setLoading(true)
    
    setTimeout(() => {
      try {
        const completeIncome: IncomeData = {
          primaryEarnerBaseSalary: income.primaryEarnerBaseSalary || 0,
          overtimeFactor: income.overtimeFactor || 0,
          bonusIncentiveAnnual: income.bonusIncentiveAnnual || 0,
          secondaryEarnerSalary: income.secondaryEarnerSalary || 0,
          sideWorkFreelanceMonthly: income.sideWorkFreelanceMonthly || 0,
          regionalCostAdjustmentFactor: income.regionalCostAdjustmentFactor || 1.0
        }

        const adjustedMonthlyIncome = calculateAdjustedMonthlyIncome(completeIncome)
        const analysis = calculateAffordabilityAnalysis(adjustedMonthlyIncome)
        
        setAffordabilityAnalysis(analysis)
      } catch (error) {
        console.error('Calculation error:', error)
        setErrors(['An error occurred during calculation. Please check your inputs and try again.'])
      } finally {
        setLoading(false)
      }
    }, 500)
  }, [income, regional])

  // Legacy calculation function
  const handleCalculate = useCallback(() => {
    setErrors([])
    
    const validationErrors = validateIncomeData(legacyIncome)
    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      return
    }

    if (!regional.state) {
      setErrors(['Please select your state'])
      return
    }

    setLoading(true)
    
    setTimeout(() => {
      try {
        const completeIncome: LegacyIncomeData = {
          baseHourlyRate: legacyIncome.baseHourlyRate || 0,
          regularHours: legacyIncome.regularHours || 40,
          overtimeHours: legacyIncome.overtimeHours || 0,
          overtimeMultiplier: legacyIncome.overtimeMultiplier || 1.5,
          weeksPerYear: legacyIncome.weeksPerYear || CALCULATION_DEFAULTS.weeksPerYear
        }

        const completeRegional: RegionalFactors = {
          state: regional.state || '',
          cityType: regional.cityType || 'suburban',
          costOfLivingIndex: regional.costOfLivingIndex || 1.0,
          averagePropertyTax: regional.averagePropertyTax || CALCULATION_DEFAULTS.propertyTaxRate,
          averageInsurance: regional.averageInsurance || CALCULATION_DEFAULTS.homeInsuranceRate
        }

        const grossMonthlyIncome = calculateGrossMonthlyIncome(completeIncome)
        const grossAnnualIncome = grossMonthlyIncome * 12
        const taxes = calculateTaxes(grossAnnualIncome, completeRegional.state)
        const netMonthlyIncome = calculateNetMonthlyIncome(grossAnnualIncome, taxes)
        const housing = calculateHousingBudget(grossMonthlyIncome, netMonthlyIncome, completeRegional)
        const recommendations = generateRecommendations(completeIncome, housing, completeRegional)

        const calculatorResults: CalculatorResults = {
          income: completeIncome as any, // Type assertion for legacy compatibility
          regional: completeRegional,
          taxes,
          housing,
          recommendations
        }

        setResults(calculatorResults)
      } catch (error) {
        console.error('Calculation error:', error)
        setErrors(['An error occurred during calculation. Please check your inputs and try again.'])
      } finally {
        setLoading(false)
      }
    }, 500)
  }, [legacyIncome, regional, tradeFactors])

  // Listen for iframe messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'TOOL_INIT') {
        const { embedded, toolId } = event.data.data || {}
        setIsEmbedded(embedded === true)
        console.log('Calculator embedded mode activated')
        
        // Send TOOL_READY response back to parent
        if (event.source && typeof event.source.postMessage === 'function') {
          try {
            (event.source as Window).postMessage({
              type: 'TOOL_READY',
              data: { toolId }
            }, event.origin)
          } catch (e) {
            console.warn('Could not send TOOL_READY message:', e)
          }
        }
      }
    }

    window.addEventListener('message', handleMessage)
    
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  // Check if we're in an iframe on initial load
  useEffect(() => {
    const checkIfEmbedded = () => {
      try {
        return window.self !== window.top
      } catch (e) {
        return true // If we can't access window.top, we're likely in an iframe
      }
    }

    // Set embedded state based on iframe detection if no message received
    const embedded = checkIfEmbedded()
    if (embedded && !isEmbedded) {
      setIsEmbedded(true)
    }
  }, [isEmbedded])

  return (
    <div className={`min-h-screen ${isEmbedded ? 'bg-white' : 'bg-neutral-50'}`}>
      {/* Header - only show when not embedded */}
      {!isEmbedded && (
        <header className="header-gradient shadow-lg border-b border-navy-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-3">
                <FaHome className="text-gold-400 text-2xl" />
                <h1 className="text-xl font-bold text-white">
                  Trade Worker Housing Affordability Calculator
                </h1>
              </div>
              <div className="flex items-center space-x-4">
                <FaCog className="text-neutral-300 hover:text-white cursor-pointer transition-colors" />
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Error Display */}
      {errors.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="alert-error">
            <h3 className="text-sm font-semibold mb-2">Please fix the following errors:</h3>
            <ul className="list-disc list-inside space-y-1">
              {errors.map((error, index) => (
                <li key={index} className="text-sm">{error}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <div className="flex space-x-1 bg-neutral-100 p-1 rounded-lg w-fit">
          <button
            className={`px-4 py-2 rounded-md text-sm transition-colors ${
              activeTab === 'new' 
                ? 'tab-active' 
                : 'tab-inactive'
            }`}
            onClick={() => setActiveTab('new')}
          >
            Enhanced Calculator
          </button>
          <button
            className={`px-4 py-2 rounded-md text-sm transition-colors ${
              activeTab === 'legacy' 
                ? 'tab-active' 
                : 'tab-inactive'
            }`}
            onClick={() => setActiveTab('legacy')}
          >
            Legacy Calculator
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'new' ? (
          <div className="space-y-8">
            {/* Real Income Calculator */}
            <RealIncomeCalculator
              income={income}
              onIncomeChange={setIncome}
              selectedState={regional.state}
            />
            
            {/* Market Comparison */}
            <MarketComparison
              comparison={marketComparison}
              onComparisonChange={setMarketComparison}
              userIncome={income.primaryEarnerBaseSalary ? calculateTotalGrossIncome(income as IncomeData) : 0}
            />
            
            {/* Calculate Button */}
            <div className="flex justify-center">
              <button 
                className="btn-primary px-8 py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleNewCalculate}
                disabled={loading || !income.primaryEarnerBaseSalary}
              >
                {loading ? 'Calculating...' : 'Calculate Affordability'}
              </button>
            </div>
            
            {/* Affordability Analysis Results */}
            {affordabilityAnalysis && (
              <AffordabilityAnalysis
                analysis={affordabilityAnalysis}
                monthlyIncome={calculateAdjustedMonthlyIncome(income as IncomeData)}
              />
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Legacy Calculator Form */}
            <div className="lg:col-span-2">
              <CalculatorForm
                income={legacyIncome}
                regional={regional}
                tradeFactors={tradeFactors}
                onIncomeChange={setLegacyIncome}
                onRegionalChange={setRegional}
                onTradeFactorsChange={setTradeFactors}
                onCalculate={handleCalculate}
                loading={loading}
              />
            </div>

            {/* Legacy Results Display */}
            <div className="lg:col-span-1">
              <ResultsDisplay
                results={results}
                loading={loading}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export default App